/**
 * Correcting the scale of colours a PLY wrote as fractions rather than as bytes.
 *
 * `PLYLoader` divides every colour channel by 255 whatever type the file declared. A file
 * whose channels are floats from 0 to 1 — which the format allows and some exporters write
 * — therefore arrives about 255 times too dark, which on screen is black. Nothing about
 * the values themselves gives this away: 1 is a legitimate byte, and a very dark one, so
 * only the declared type in the header distinguishes the two, which is why `ModelStats`
 * carries it.
 *
 * The correction cannot be a multiplication of what came out. The loader also converts
 * from sRGB on the way in and that conversion is not linear, so the value has to go back
 * to sRGB, be restored to the range the loader expected, and be converted afresh.
 *
 * A correction rather than a parser of our own, because everything else `PLYLoader` does
 * with colours is right — the property name aliases, the per-face case, the colour space —
 * and reimplementing all of that to fix the scale would be a poor trade.
 *
 * In its own file so that it can be tested: parsing and colour arithmetic need no WebGL,
 * whereas `SceneManager` builds a renderer.
 */

import { BufferAttribute, Color, SRGBColorSpace, type BufferGeometry } from 'three';

import type { ModelStats } from '../domain/types.js';

/** Returns the same geometry, corrected in place where it needed it. */
export function correctColourScale(
  geometry: BufferGeometry,
  stats: ModelStats,
): BufferGeometry {
  // `hasAttribute` rather than a test of what `getAttribute` returned: three types the
  // latter as always yielding an attribute, so checking for its absence reads to the
  // linter as dead code even though a PLY without colours produces exactly that.
  if (stats.fileColours !== 'unit' || !geometry.hasAttribute('color')) return geometry;

  const colours = geometry.getAttribute('color');
  const working = new Color();
  const asSrgb = { r: 0, g: 0, b: 0 };

  for (let index = 0; index < colours.count; index += 1) {
    working.setRGB(colours.getX(index), colours.getY(index), colours.getZ(index));
    working.getRGB(asSrgb, SRGBColorSpace);
    working.setRGB(asSrgb.r * 255, asSrgb.g * 255, asSrgb.b * 255, SRGBColorSpace);
    colours.setXYZ(index, working.r, working.g, working.b);
  }

  colours.needsUpdate = true;
  return geometry;
}

/**
 * Give a PLY's geometry the opacity its file declared, if it declared any.
 *
 * three's loader reads red, green and blue and stops, so a PLY that says how see-through
 * it is has been saying so to nobody. The values are supplied here instead and the colour
 * attribute widened to carry them: a four-component colour attribute is something three
 * renders directly, given a material told to expect transparency.
 *
 * Returns whether anything is actually translucent, since a file may declare an alpha
 * channel and fill it with ones — and a material marked transparent for no reason joins a
 * render pass it has no business in.
 */
export function applyPlyAlpha(
  geometry: BufferGeometry,
  alpha: Float32Array | undefined,
): boolean {
  if (!alpha || !geometry.hasAttribute('color')) return false;

  const colours = geometry.getAttribute('color');
  if (colours.itemSize !== 3 || colours.count !== alpha.length) return false;
  if (alpha.every((value) => value >= 1)) return false;

  const widened = new Float32Array(colours.count * 4);
  for (let index = 0; index < colours.count; index += 1) {
    widened[index * 4] = colours.getX(index);
    widened[index * 4 + 1] = colours.getY(index);
    widened[index * 4 + 2] = colours.getZ(index);
    widened[index * 4 + 3] = alpha[index] ?? 1;
  }

  geometry.setAttribute('color', new BufferAttribute(widened, 4));
  return true;
}
